import { motion } from "framer-motion";
import { Check } from "lucide-react";
import Seo from "@/components/Seo";
import { CtaSection } from "@/components/CtaSection";

const PRINCIPLES = [
  "Growth first, ego second",
  "No templated, one-size-fits-all campaigns",
  "Strategy that sells, not just looks pretty",
  "Transparent reporting, real conversations",
];

export default function About() {
  return (
    <>
      <Seo
        title="About — Covelli"
        siteName="Covelli"
        description="A Toronto-based marketing agency built for brands that want to scale, not just post."
      />
      <section data-testid="about-hero" className="mx-auto max-w-7xl px-6 pb-6 pt-32 md:pt-40">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-kinetic"
        >
          About
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
          className="font-display text-4xl font-extrabold tracking-tighter text-slate-900 sm:text-5xl md:text-6xl"
        >
          About Covelli
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
          className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-600"
        >
          A Toronto-based marketing agency built for brands that want to scale,
          not just post.
        </motion.p>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 pt-8">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <h2 className="font-display text-2xl font-bold text-slate-900 sm:text-3xl">Why Covelli exists</h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600">
              Covelli exists because too many brands have been burned by vanity
              metrics and templated, one-size-fits-all campaigns. We built the
              agency we wished existed: strategy that sells, creative that
              stops the scroll, and performance reporting that talks about
              revenue — all from Toronto, for ambitious teams who want to move
              fast, think differently, and win loudly.
            </p>
            <h3 className="mt-12 font-mono text-[11px] uppercase tracking-[0.3em] text-skybrand">
              How we think
            </h3>
            <ul className="mt-5 space-y-4">
              {PRINCIPLES.map((p) => (
                <li key={p} className="flex items-start gap-3 text-base text-slate-700">
                  <Check size={17} className="mt-1 shrink-0 text-skybrand" />
                  {p}
                </li>
              ))}
            </ul>
            <h3 className="mt-14 font-display text-xl font-bold text-slate-900">Founder-led, hands-on</h3>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Covelli is led hands-on by its founder — an operator with
              experience across paid media, creative and growth for brands in
              ecommerce, SaaS and local services. No account managers, no
              hand-offs: the person on your strategy call is the person in your
              ad account.
            </p>
          </motion.div>
        </div>
      </section>

      <CtaSection
        testid="about-cta"
        headline="Want to see if we're a fit?"
        subtext="One honest conversation is worth a dozen proposals."
        buttonText="Book a Call"
      />
    </>
  );
}
